import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  ActivationEnd,
  NavigationEnd,
  NavigationStart,
  Router
} from '@angular/router';
import { CommunicatorService } from '@app/shared/services/communicator.service';
import { TranslateService } from '@ngx-translate/core';
import { DateService } from '@shared/services/date.service';
import { FeatureService } from '@shared/services/feature.service';
import { LanguageService } from '@shared/services/language.service';
import { ThemeLoaderService } from '@shared/services/theme-loader.service';
import { IframeCoordinatorClientService } from '@shared/services/iframe-coordinator-client.service';

import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';
import './globals';
import { CustomRouteData } from './custom-route.types';

interface IRouteData {
  event: NavigationEnd;
  route: ActivatedRoute;
  data?: CustomRouteData;
}

/** Base component for the app */
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  public destroy$: Subject<boolean> = new Subject<boolean>();
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly router: Router,
    private readonly featureService: FeatureService,
    private readonly themeService: ThemeLoaderService,
    private readonly translate: TranslateService,
    private readonly communicator: CommunicatorService,
    private readonly titleService: Title,
    private readonly activatedRoute: ActivatedRoute,
    private readonly languageService: LanguageService,
    private readonly ifcClientService: IframeCoordinatorClientService,
    private readonly dateService: DateService
  ) {}

  public ngOnInit(): void {
    this.languageService.init();

    this.setupPageTitleSubscription();

    this.initializeAppTheme();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.destroy$.next(true);
  }

  /** Handle Page Titles based off Angular route's data.title prop */
  private setupPageTitleSubscription(): void {
    const hasIfcMigrationFeature$ = this.featureService.hasFeature('adminUI.v3.IFC.migration');
    const routeData$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      mergeMap((event: NavigationEnd) => this.toRouteData(event)),
      filter((routeData) => routeData.route.outlet === 'primary'),
      filter((routeData) => routeData.data.hasOwnProperty('page'))
    );

    const subscription = combineLatest([routeData$, hasIfcMigrationFeature$]).subscribe(
      ([routeData, hasIfcMigrationFeature]) => {
        if (hasIfcMigrationFeature) {
          this.ifcClientService.updatePageData(routeData.data);
        } else if (routeData.data.legacy?.title) {
          this.setTitle(routeData);
        }
      }
    );

    // Merge subscription to unsubscribe during ngOnDestroy
    this.subscriptions.add(subscription);
  }

  /** Combine NavigationEnd event and nested firstChild of ActivatedRoute into a new RouteData observable */
  private toRouteData(event: NavigationEnd): Observable<IRouteData> {
    let route = this.activatedRoute;

    while (route.firstChild) {
      route = route.firstChild;
    }

    return route.data.pipe(map((data) => ({ event, route, data })));
  }

  /**
   * Set the page title from a RouteData object
   * Route change will also be broadcast to parent if app is embedded
   */
  private setTitle(routeData: IRouteData): void {
    debug('router', 'router event', routeData.event);
    let baseTitle = this.translate.instant('v3.nav.admin');
    let eventTitle = this.translate.instant(routeData.data.legacy.title);
    let pageTitle = `${eventTitle} - ${baseTitle}`;
    debug(
      'translate',
      `translated page title label "${routeData.data.legacy.title}" -> "${eventTitle}"`
    );

    this.titleService.setTitle(pageTitle);

    if (this.communicator.appRunningInIframe) {
      this.communicator.routeChangeSuccess(routeData.event.url, pageTitle);
    }
  }

  /** Sets up theme subscription and initializes using theme service */
  private initializeAppTheme(): void {
    const subscription = this.featureService
      .hasFeature('genesysUIBranding')
      .subscribe((genesysUIBranding) => {
        this.themeService.setThemeClass({
          theme: this.themeService.loadCachedTheme(document.cookie)
        });
        this.themeService.validateThemeAgainstFeatureToggles(genesysUIBranding);
      });

    // Merge subscription to unsubscribe during ngOnDestroy
    this.subscriptions.add(subscription);
  }
}
